import { useEffect, useId, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { FormCheckbox } from '../../components/FormField'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { InactiveToggle } from '../../components/InactiveToggle'
import { RecordLink } from '../../components/RecordLink'
import { ApiError } from '../../lib/api'
import { apiErrorMessage } from '../../lib/errors'
import { formatDate, formatMoney } from '../../lib/format'
import { ComplianceBadge } from '../brokers/ComplianceBadge'
import { getBroadcastDraft, sendBroadcast, type BroadcastDraft, type DraftAgent, type DraftBroker } from './api'
import { modeText, reasonText } from './describe'
import { MessagePreview } from './MessagePreview'
import { unitName } from '../availability/unitName'

/**
 * Tells brokers about a posting (Stage 2, 2.4): every agent in the book, grouped by broker,
 * with the ones this broadcast can reach ticked and the rest explained; the email as they
 * would read it; and one button. The screen explains, the server judges: a send refused
 * because the book moved names each agent, and the list is read again so the rows say why.
 *
 * <p>In Recording — the mode of both environments for now — nothing is sent: every message
 * is composed and kept, and the broadcast's page offers each one as an .eml.</p>
 */
/** Nobody the posting can reach for good: an agent we stopped working with, or one of a broker we did. */
function isInactive(broker: DraftBroker, agent: DraftAgent): boolean {
  return !broker.active || !agent.active
}

export function BroadcastPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id: postingId } = useParams()
  const searchId = useId()

  const [draft, setDraft] = useState<BroadcastDraft | null>(null)
  const [failure, setFailure] = useState<{ error: unknown } | null>(null)
  const [includeMinimumRate, setIncludeMinimumRate] = useState(false)
  // Null until the first reading: then every agent it can reach, and the dispatcher's choices after.
  const [chosen, setChosen] = useState<ReadonlySet<string> | null>(null)
  const [search, setSearch] = useState('')
  const [refusal, setRefusal] = useState<unknown>(null)
  const [isReading, setIsReading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [reading, setReading] = useState(0)

  // The draft, read again whenever the minimum rate is ticked (it changes the message) or a
  // send was refused (the book moved). Only the latest answer writes.
  useEffect(() => {
    if (postingId === undefined) {
      return
    }

    let active = true
    setIsReading(true)

    getBroadcastDraft(postingId, includeMinimumRate)
      .then((found) => {
        if (!active) {
          return
        }

        const reachable = new Set(
          found.brokers.flatMap((broker) => broker.agents).filter((agent) => agent.eligible).map((agent) => agent.agentId),
        )

        setDraft(found)
        setFailure(null)
        // A later reading keeps what the dispatcher chose, less anyone it can no longer reach.
        setChosen((current) => (current === null ? reachable : new Set([...current].filter((id) => reachable.has(id)))))
      })
      .catch((caught: unknown) => {
        if (active) {
          setFailure({ error: caught })
        }
      })
      .finally(() => {
        if (active) {
          setIsReading(false)
        }
      })

    return () => {
      active = false
    }
  }, [postingId, includeMinimumRate, reading])

  const reachableCount = useMemo(
    () => draft?.brokers.flatMap((broker) => broker.agents).filter((agent) => agent.eligible).length ?? 0,
    [draft],
  )

  // Inactive brokers and agents — nobody a posting can reach — are held back until asked for
  // (TRKC-110 item 2). The search looks among what is shown.
  const [showInactive, setShowInactive] = useState(false)
  const inactiveCount = useMemo(
    () => draft?.brokers.flatMap((broker) => broker.agents.filter((agent) => isInactive(broker, agent))).length ?? 0,
    [draft],
  )

  const shown = useMemo(() => {
    if (draft === null) {
      return []
    }

    const listed = showInactive
      ? draft.brokers
      : draft.brokers
          .map((broker) => ({ ...broker, agents: broker.agents.filter((agent) => !isInactive(broker, agent)) }))
          .filter((broker) => broker.agents.length > 0)

    const term = search.trim().toLowerCase()
    if (term === '') {
      return listed
    }

    return listed
      .map((broker) => {
        if (broker.name.toLowerCase().includes(term)) {
          return broker
        }

        const agents = broker.agents.filter(
          (agent) => agent.name.toLowerCase().includes(term) || agent.email.toLowerCase().includes(term),
        )
        return agents.length === 0 ? null : { ...broker, agents }
      })
      .filter((broker): broker is DraftBroker => broker !== null)
  }, [draft, search, showInactive])

  if (draft === null) {
    return failure === null ? (
      <p className="session-check">{t('broadcasts.loading')}</p>
    ) : (
      <section className="page">
        <Breadcrumbs
          trail={[
            { label: t('nav.availability'), to: '/availability' },
            { label: null, to: `/availability/${postingId}` },
            { label: t('broadcasts.start') },
          ]}
        />
        <p className="alert" role="alert">
          {apiErrorMessage(failure.error, t, 'broadcasts.loadError')}
        </p>
        {postingId !== undefined && (
          <p>
            <Link to={`/availability/${postingId}`}>{t('broadcasts.backToPosting')}</Link>
          </p>
        )}
      </section>
    )
  }

  const { posting } = draft
  const language = i18n.language
  const selectedCount = chosen?.size ?? 0
  const records = draft.mode === 'Recording'
  // Every change of choice reads the draft again. While that reading is on its way — or when
  // it failed, leaving the older draft on screen (an email without the minimum rate just
  // ticked, say) — what is shown is not what would go, and nothing may (Copilot on #44).
  const shownIsCurrent = !isReading && failure === null

  function toggle(agent: DraftAgent, checked: boolean) {
    setChosen((current) => {
      const next = new Set(current ?? [])
      if (checked) {
        next.add(agent.agentId)
      } else {
        next.delete(agent.agentId)
      }
      return next
    })
  }

  function chooseAll(all: boolean) {
    setChosen(
      all
        ? new Set(draft!.brokers.flatMap((broker) => broker.agents).filter((a) => a.eligible).map((a) => a.agentId))
        : new Set(),
    )
  }

  async function send() {
    if (postingId === undefined || chosen === null) {
      return
    }

    setIsSending(true)
    setRefusal(null)

    try {
      // The version of the email on screen goes with it: one that would no longer read so — the
      // posting edited since — is refused, and read again (GPT-6.1 Sol on #44).
      const created = await sendBroadcast(postingId, {
        agentIds: [...chosen],
        includeMinimumRate,
        messageVersion: draft!.messageVersion,
      })
      void navigate(`/availability/${postingId}/broadcasts/${created.id}`)
    } catch (caught) {
      setRefusal(caught)
      // A refusal usually means the book or the posting moved: read it again, so every
      // row says where it stands now.
      setReading((current) => current + 1)
    } finally {
      setIsSending(false)
    }
  }

  const blockedReasons: string[] = []
  if (draft.mode === 'Disabled') {
    blockedReasons.push(t('broadcasts.mailDisabled'))
  }
  if (posting.standing !== 'Open') {
    blockedReasons.push(t('broadcasts.postingNotOpen'))
  }

  return (
    <section className="page">
      <Breadcrumbs
        trail={[
          { label: t('nav.availability'), to: '/availability' },
          { label: draft.reference, to: `/availability/${postingId}` },
          { label: t('broadcasts.start') },
        ]}
      />
      <header className="page__header page__header--row">
        <div>
          <h1>{t('broadcasts.title', { reference: draft.reference })}</h1>
          <p className="page__subtitle">
            {[
              unitName(posting, t),
              posting.carrierName,
              `${posting.city}, ${posting.state}`,
              t('availability.fromUntil', {
                from: formatDate(posting.availableFrom, language),
                until: formatDate(posting.availableUntil, language),
              }),
            ].join(' · ')}
          </p>
        </div>
        <Link className="button button--quiet" to={`/availability/${posting.id}`}>
          {t('broadcasts.backToPosting')}
        </Link>
      </header>

      <p className="broadcast-mode" role="status">
        <strong>{modeText(draft.mode, t)}</strong> {t(`broadcasts.modeNotes.${draft.mode}`)}
      </p>

      {blockedReasons.length > 0 && (
        <div className="alert alert--warning" role="status">
          {blockedReasons.map((reason) => (
            <p key={reason}>{reason}</p>
          ))}
        </div>
      )}

      {/* The gate the send asks again: a unit that cannot be offered today, named. */}
      {posting.standing === 'Open' && posting.heldBackBy.length > 0 && (
        <div className="alert alert--warning" role="status">
          <p>{t('broadcasts.heldBack')}</p>
          {posting.heldBackBy.map((holdback) => (
            <p key={holdback.unit}>
              {t('availability.heldBackBy', {
                unit: t(`availability.units.${holdback.unit}`),
                name: holdback.culprit.name,
              })}{' '}
              <RecordLink subject={holdback.culprit} />
            </p>
          ))}
        </div>
      )}

      {refusal !== null && <Refusal caught={refusal} />}

      {failure !== null && (
        <div className="alert" role="alert">
          <p>{t('broadcasts.draftUnread')}</p>
          <button type="button" className="button button--quiet" onClick={() => setReading((current) => current + 1)}>
            {t('broadcasts.readAgain')}
          </button>
        </div>
      )}

      <section className="broadcast-section broadcast-recipients" aria-labelledby={`${searchId}-heading`}>
        <h2 id={`${searchId}-heading`}>{t('broadcasts.recipients.title')}</h2>

        {draft.brokers.length === 0 ? (
          <p className="placeholder">
            {t('broadcasts.recipients.empty')} <Link to="/brokers">{t('broadcasts.recipients.toBrokers')}</Link>
          </p>
        ) : (
          <>
            <p role="status">{t('broadcasts.recipients.chosen', { chosen: selectedCount, reachable: reachableCount })}</p>
            <div className="broadcast-recipients__tools">
              <label htmlFor={searchId} className="visually-hidden">
                {t('broadcasts.recipients.search')}
              </label>
              <input
                id={searchId}
                type="search"
                className="broadcast-recipients__search"
                placeholder={t('broadcasts.recipients.search')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <button type="button" className="button button--quiet" onClick={() => chooseAll(true)}>
                {t('broadcasts.recipients.all')}
              </button>
              <button type="button" className="button button--quiet" onClick={() => chooseAll(false)}>
                {t('broadcasts.recipients.none')}
              </button>
            </div>

            <div id={`${searchId}-list`}>
              {shown.length === 0 && (
                <p className="placeholder">
                  {search.trim() === '' ? t('broadcasts.recipients.noneInUse') : t('broadcasts.recipients.noMatch')}
                </p>
              )}

              {shown.map((broker) => (
                <div key={broker.brokerId} className="broadcast-recipients__broker">
                  <h3>
                    <Link to={`/brokers/${broker.brokerId}`}>{broker.name}</Link>{' '}
                    <ComplianceBadge state={broker.complianceState} />
                    {!broker.active && <span className="badge badge--inactive">{t('activation.inactive')}</span>}
                  </h3>
                  <ul className="broadcast-recipients__agents">
                    {broker.agents.map((agent) => {
                      const why = reasonText(agent, t, language)
                      return (
                        <li key={agent.agentId} className="broadcast-recipients__agent">
                          <FormCheckbox
                            name={`agent-${agent.agentId}`}
                            label={
                              agent.title === null
                                ? `${agent.name} · ${agent.email}`
                                : `${agent.name} (${agent.title}) · ${agent.email}`
                            }
                            checked={agent.eligible && (chosen?.has(agent.agentId) ?? false)}
                            onChange={(checked) => toggle(agent, checked)}
                            disabled={!agent.eligible}
                          />
                          {why !== null && <p className="broadcast-recipients__reason">{why}</p>}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
            <InactiveToggle
              kind="recipients"
              controls={`${searchId}-list`}
              count={inactiveCount}
              shown={showInactive}
              onToggle={() => setShowInactive((shown) => !shown)}
            />
          </>
        )}
      </section>

      {posting.minRatePerMile !== null && (
        <div className="broadcast-section">
          <FormCheckbox
            name="includeMinimumRate"
            label={t('broadcasts.includeMinimumRate', {
              rate: formatMoney(posting.minRatePerMile, 'USD', language),
            })}
            checked={includeMinimumRate}
            onChange={setIncludeMinimumRate}
          />
        </div>
      )}

      <MessagePreview message={draft.message} />

      <div className="form__actions">
        <Link className="button button--quiet" to={`/availability/${posting.id}`}>
          {t('broadcasts.cancel')}
        </Link>
        <button
          type="button"
          className="button"
          onClick={() => void send()}
          disabled={!draft.canSend || selectedCount === 0 || isSending || !shownIsCurrent}
        >
          {isSending
            ? t('broadcasts.sending')
            : t(records ? 'broadcasts.record' : 'broadcasts.send', { count: selectedCount })}
        </button>
      </div>
    </section>
  )
}

/** A refused send, every reason in its own sentence — and the agent or unit it names, one click away. */
function Refusal({ caught }: { caught: unknown }) {
  const { t } = useTranslation()

  if (!(caught instanceof ApiError) || caught.problems.length === 0) {
    return (
      <p className="alert" role="alert">
        {apiErrorMessage(caught, t, 'broadcasts.sendError')}
      </p>
    )
  }

  return (
    <div className="alert" role="alert">
      {caught.problems.map((problem, index) => {
        const key = `errors.${problem.code}`
        const sentence = t(key, { name: problem.subject?.name ?? t('registry.anotherRecord') })
        return (
          <p key={`${problem.code}-${problem.subject?.id ?? index}`}>
            {sentence === key ? t('broadcasts.sendError') : sentence}{' '}
            {problem.subject !== null && <RecordLink subject={problem.subject} />}
          </p>
        )
      })}
    </div>
  )
}
